/**
 * PointsBreakdown Component
 * Detailed breakdown card showing points by category, stats, and badges
 * Expandable/collapsible for detailed view
 */

import React, { useState } from 'react';
import { Award, TrendingUp, Star, Users, Percent, ChevronDown, ChevronUp } from 'lucide-react';
import { useSellerPointsDetailed } from '../../hooks/usePoints';

const PointsBreakdown = ({ sellerId, initialExpanded = false }) => {
  const [isExpanded, setIsExpanded] = useState(initialExpanded);
  const { data, isLoading, error } = useSellerPointsDetailed(sellerId);

  if (isLoading) {
    return (
      <div className="bg-white rounded-lg border border-slate-200 p-4 animate-pulse">
        <div className="h-6 bg-slate-200 rounded w-32 mb-2"></div>
        <div className="h-4 bg-slate-200 rounded w-24"></div>
      </div>
    );
  }

  if (error || !data) {
    return null;
  }

  const { points_total, breakdown, stats, badges } = data;

  return (
    <div className="bg-white rounded-lg border border-slate-200 shadow-sm">
      {/* Header - Always Visible */}
      <div
        className="p-4 cursor-pointer hover:bg-slate-50 transition-colors"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="bg-amber-100 p-2 rounded-lg">
              <Award className="h-6 w-6 text-amber-600" />
            </div>
            <div>
              <div className="flex items-baseline gap-2">
                <span className="text-3xl font-bold text-slate-900">{points_total}</span>
                <span className="text-sm text-slate-500">total points</span>
              </div>
              {badges && badges.length > 0 && (
                <div className="flex gap-2 mt-1">
                  {badges.map((badge, index) => (
                    <span
                      key={index}
                      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-700"
                    >
                      {badge === 'Reorder Champion' && '🏆'}
                      {badge === 'Top Rated' && '⭐'}
                      {badge}
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>
          {isExpanded ? (
            <ChevronUp className="h-5 w-5 text-slate-400" />
          ) : (
            <ChevronDown className="h-5 w-5 text-slate-400" />
          )}
        </div>
      </div>

      {/* Expanded Details */}
      {isExpanded && (
        <div className="border-t border-slate-200 p-4 space-y-4">
          {/* Points Breakdown */}
          <div>
            <h4 className="text-sm font-semibold text-slate-700 mb-3">Points Breakdown</h4>
            <div className="space-y-2">
              <BreakdownRow
                icon={<Users className="h-4 w-4" />}
                label="Initial Orders"
                points={breakdown.initial_orders}
                color="blue"
              />
              <BreakdownRow
                icon={<TrendingUp className="h-4 w-4" />}
                label="Reorders"
                points={breakdown.reorders}
                color="green"
              />
              <BreakdownRow
                icon={<Star className="h-4 w-4" />}
                label="Reviews"
                points={breakdown.reviews}
                color="amber"
              />
              {breakdown.bonuses > 0 && (
                <BreakdownRow
                  icon={<Award className="h-4 w-4" />}
                  label="Bonuses"
                  points={breakdown.bonuses}
                  color="purple"
                />
              )}
            </div>
          </div>

          {/* Stats */}
          <div>
            <h4 className="text-sm font-semibold text-slate-700 mb-3">Performance Stats</h4>
            <div className="grid grid-cols-2 gap-3">
              <StatCard
                label="Reorder Rate"
                value={`${stats.reorder_percentage.toFixed(1)}%`}
                icon={<Percent className="h-4 w-4" />}
                highlight={stats.reorder_percentage > 50}
              />
              <StatCard
                label="Avg Rating"
                value={`${stats.avg_rating.toFixed(1)} / 6`}
                icon={<Star className="h-4 w-4" />}
                highlight={stats.avg_rating > 4.0}
              />
              <StatCard
                label="Total Orders"
                value={stats.total_orders}
                icon={<Users className="h-4 w-4" />}
              />
              <StatCard
                label="Repeat Buyers"
                value={stats.repeat_buyers}
                icon={<TrendingUp className="h-4 w-4" />}
              />
            </div>
          </div>

          {/* Bonus Status */}
          {(stats.reorder_percentage > 45 || stats.avg_rating > 3.8) && (
            <div className="bg-slate-50 rounded-lg p-3">
              <h4 className="text-xs font-semibold text-slate-600 mb-2">Bonus Status</h4>
              <div className="space-y-1 text-xs text-slate-600">
                {stats.reorder_percentage > 50 ? (
                  <div className="flex items-center gap-2">
                    <span className="text-green-600">✓</span>
                    <span>Reorder bonus active (>50%)</span>
                  </div>
                ) : stats.reorder_percentage > 45 ? (
                  <div className="flex items-center gap-2">
                    <span className="text-amber-600">→</span>
                    <span>{(50 - stats.reorder_percentage).toFixed(1)}% away from reorder bonus</span>
                  </div>
                ) : null}
                
                {stats.avg_rating > 4.0 ? (
                  <div className="flex items-center gap-2">
                    <span className="text-green-600">✓</span>
                    <span>Rating bonus active (>4.0)</span>
                  </div>
                ) : stats.avg_rating > 3.8 ? (
                  <div className="flex items-center gap-2">
                    <span className="text-amber-600">→</span>
                    <span>{(4.0 - stats.avg_rating).toFixed(2)} away from rating bonus</span>
                  </div>
                ) : null}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Helper Components

const BreakdownRow = ({ icon, label, points, color }) => {
  const colorClasses = {
    blue: 'bg-blue-100 text-blue-600',
    green: 'bg-green-100 text-green-600',
    amber: 'bg-amber-100 text-amber-600',
    purple: 'bg-purple-100 text-purple-600',
  };

  return (
    <div className="flex items-center justify-between py-2">
      <div className="flex items-center gap-2">
        <div className={`p-1.5 rounded ${colorClasses[color]}`}>{icon}</div>
        <span className="text-sm text-slate-700">{label}</span>
      </div>
      <span className="text-sm font-semibold text-slate-900">{points}</span>
    </div>
  );
};

const StatCard = ({ label, value, icon, highlight = false }) => {
  return (
    <div className={`p-3 rounded-lg border ${highlight ? 'border-purple-200 bg-purple-50' : 'border-slate-200 bg-white'}`}>
      <div className="flex items-center gap-2 mb-1">
        <div className={`${highlight ? 'text-purple-600' : 'text-slate-500'}`}>{icon}</div>
        <span className="text-xs text-slate-600">{label}</span>
      </div>
      <div className={`text-lg font-bold ${highlight ? 'text-purple-700' : 'text-slate-900'}`}>
        {value}
      </div>
    </div>
  );
};

export default PointsBreakdown;
